import { showToast } from "./utils/toast";

export type ColumnWidths = Record<string, number>;

export interface ColumnPrefs {
  order: string[] | null; // null = the default order
  hidden: string[];
  widths?: ColumnWidths; // px per resized column; a column without one stays elastic
}

const LOCAL_PREFIX = "candidatesColumnOrder:";
const SAVE_DELAY_MS = 300;

const saveTimers = new WeakMap<HTMLTableElement, number>();

const localKey = (table: HTMLTableElement): string => LOCAL_PREFIX + (table.dataset.columnsKey || "all");

const toStrings = (value: unknown): string[] | null => (Array.isArray(value) ? value.map(String) : null);

export function toWidths(value: unknown): ColumnWidths {
  const out: ColumnWidths = {};
  if (!value || typeof value !== "object" || Array.isArray(value)) return out;

  Object.entries(value as Record<string, unknown>).forEach(([key, px]) => {
    const n = Number(px);
    if (Number.isFinite(n) && n > 0) out[key] = Math.round(n);
  });
  return out;
}

function readLocalOrder(table: HTMLTableElement): string[] | null {
  try {
    return toStrings(JSON.parse(localStorage.getItem(localKey(table)) || "null"));
  } catch (_e) {
    return null;
  }
}

function writeLocalOrder(table: HTMLTableElement, order: string[] | null): void {
  try {
    if (order) localStorage.setItem(localKey(table), JSON.stringify(order));
    else localStorage.removeItem(localKey(table));
  } catch (_e) { }
}

const onServer = (table: HTMLTableElement): boolean => Boolean(table.dataset.columnsUrl);

function readServerState(table: HTMLTableElement): ColumnPrefs {
  try {
    const state = JSON.parse(table.dataset.columnsState || "{}") || {};
    return { order: toStrings(state.order), hidden: toStrings(state.hidden) || [], widths: toWidths(state.widths) };
  } catch (_e) {
    return { order: null, hidden: [], widths: {} };
  }
}

export function loadPrefs(table: HTMLTableElement): ColumnPrefs {
  if (!onServer(table)) return { order: readLocalOrder(table), hidden: [] };

  const prefs = readServerState(table);
  if (prefs.order) return prefs;

  // An order this browser saved before layouts moved to the server: adopt it once.
  const legacy = readLocalOrder(table);
  if (!legacy) return prefs;

  writeLocalOrder(table, null);
  const adopted = { order: legacy, hidden: prefs.hidden, widths: prefs.widths };
  savePrefs(table, adopted);
  return adopted;
}

export function savePrefs(table: HTMLTableElement, prefs: ColumnPrefs): void {
  if (!onServer(table)) {
    writeLocalOrder(table, prefs.order);
    return;
  }

  // The table keeps the layout last sent, so a Turbo restore of this page comes back with it.
  // A commit that changes nothing (a column dropped where it was picked up, Show all with
  // nothing hidden) sends nothing: an older tab can't write its layout back that way.
  const state = JSON.stringify({ order: prefs.order, hidden: prefs.hidden, widths: prefs.widths || {} });
  if (state === JSON.stringify(readServerState(table))) return;
  table.dataset.columnsState = state;

  window.clearTimeout(saveTimers.get(table));
  saveTimers.set(table, window.setTimeout(() => sendToServer(table, prefs), SAVE_DELAY_MS));
}

function sendToServer(table: HTMLTableElement, prefs: ColumnPrefs): void {
  const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";

  fetch(table.dataset.columnsUrl || "", {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
    body: JSON.stringify({ config: { order: prefs.order || [], hidden: prefs.hidden, widths: prefs.widths || {} } }),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    })
    // The page keeps the layout either way; the next change sends the full state again.
    .catch(() => showToast("Couldn't save your column layout", "error"));
}
